import { useEffect, useState } from "react";
import { Plus, Loader2, Pencil, Trash2, X, Check } from "lucide-react";
import api, { apiErrorMessage } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { roleLabel } from "../../lib/roles";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";

const RoleCheckboxes = ({ selected, onChange, roles, idPrefix }) => (
  <div className="sm:col-span-2 space-y-1.5">
    <label className="text-xs font-mono uppercase tracking-widest text-slate-400">Rôles (sélection multiple)</label>
    <div className="flex flex-wrap gap-2">
      {roles.map((r) => {
        const checked = selected.includes(r.value);
        return (
          <button
            key={r.value}
            type="button"
            data-testid={`${idPrefix}-role-checkbox-${r.value}`}
            onClick={() => onChange(checked ? selected.filter((v) => v !== r.value) : [...selected, r.value])}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${
              checked ? "bg-cyan-500 text-black border-cyan-500" : "border-slate-700 text-slate-400 hover:border-slate-500"
            }`}
          >
            {r.label}
          </button>
        );
      })}
    </div>
  </div>
);

const SiteCheckboxes = ({ selected, onChange, sites, idPrefix }) => (
  <div className="sm:col-span-2 space-y-1.5">
    <label className="text-xs font-mono uppercase tracking-widest text-slate-400">Sites (sélection multiple)</label>
    <div className="flex flex-wrap gap-2">
      {sites.map((s) => {
        const checked = selected.includes(s);
        return (
          <button
            key={s}
            type="button"
            data-testid={`${idPrefix}-site-checkbox-${s}`}
            onClick={() => onChange(checked ? selected.filter((v) => v !== s) : [...selected, s])}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${
              checked ? "bg-emerald-500 text-black border-emerald-500" : "border-slate-700 text-slate-400 hover:border-slate-500"
            }`}
          >
            {s}
          </button>
        );
      })}
    </div>
  </div>
);

export default function AdminUsers() {
  const { sites, rolesCatalog } = useAuth();
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ nom: "", prenom: "", email: "", roles: ["INSPECTEUR"], sites: [] });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/users").then(({ data }) => setUsers(data));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (sites.length && form.sites.length === 0) setForm((f) => ({ ...f, sites: [sites[0]] }));
  }, [sites]); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async () => {
    setError(""); setBusy(true);
    try {
      await api.post("/admin/users", { nom: form.nom, prenom: form.prenom, email: form.email, role: form.roles, site: form.sites });
      setForm({ nom: "", prenom: "", email: "", roles: ["INSPECTEUR"], sites: sites[0] ? [sites[0]] : [] });
      setShowForm(false);
      load();
    } catch (e) {
      setError(apiErrorMessage(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const toggleActif = async (u) => {
    await api.put(`/admin/users/${u.Utilisateur_ID}`, { actif: String(u.Actif).toUpperCase() !== "TRUE" });
    load();
  };

  const startEdit = (u) => {
    setEditingId(u.Utilisateur_ID);
    setEditForm({ nom: u.Nom, prenom: u.Prenom, email: u.Email, roles: u.Roles || [], sites: u.Sites || [] });
    setError("");
  };

  const saveEdit = async () => {
    setError(""); setBusy(true);
    try {
      await api.put(`/admin/users/${editingId}`, { nom: editForm.nom, prenom: editForm.prenom, email: editForm.email, role: editForm.roles, site: editForm.sites });
      setEditingId(null);
      load();
    } catch (e) {
      setError(apiErrorMessage(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const remove = async (u) => {
    setBusy(true);
    try {
      await api.delete(`/admin/users/${u.Utilisateur_ID}`);
      setDeletingId(null);
      load();
    } catch (e) {
      setError(apiErrorMessage(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button data-testid="admin-users-add-button" onClick={() => setShowForm(!showForm)} className="bg-cyan-500 hover:bg-cyan-400 text-black font-bold uppercase text-xs">
          <Plus className="w-4 h-4 mr-1.5" /> Ajouter un utilisateur
        </Button>
      </div>
      {showForm && (
        <div className="rounded-xl border border-slate-800 bg-[#121820] p-4 grid sm:grid-cols-2 gap-3">
          <Input data-testid="admin-user-prenom-input" placeholder="Prénom" value={form.prenom} onChange={(e) => setForm({ ...form, prenom: e.target.value })} className="bg-[#0A0D12] border-slate-700 text-slate-100" />
          <Input data-testid="admin-user-nom-input" placeholder="Nom" value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} className="bg-[#0A0D12] border-slate-700 text-slate-100" />
          <Input data-testid="admin-user-email-input" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#0A0D12] border-slate-700 text-slate-100 sm:col-span-2" />
          <RoleCheckboxes selected={form.roles} onChange={(roles) => setForm({ ...form, roles })} roles={rolesCatalog} idPrefix="admin-user" />
          <SiteCheckboxes selected={form.sites} onChange={(sitesSel) => setForm({ ...form, sites: sitesSel })} sites={sites} idPrefix="admin-user" />
          <div className="sm:col-span-2 text-xs text-slate-500">L'utilisateur se connectera avec son compte Google (n'importe quel domaine) — aucun mot de passe requis. Son email doit correspondre exactement à celui saisi ci-dessus.</div>
          {error && <div className="sm:col-span-2 text-sm text-red-400">{error}</div>}
          <Button data-testid="admin-user-submit-button" disabled={busy || form.roles.length === 0 || form.sites.length === 0} onClick={create} className="sm:col-span-2 bg-emerald-500 hover:bg-emerald-400 text-black font-bold uppercase text-xs">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Créer"}
          </Button>
        </div>
      )}
      <div className="space-y-2">
        {users.map((u) => (
          <div key={u.Utilisateur_ID} data-testid={`admin-user-row-${u.Utilisateur_ID}`} className="rounded-lg border border-slate-800 bg-[#121820] p-3">
            {editingId === u.Utilisateur_ID ? (
              <div className="grid sm:grid-cols-2 gap-2">
                <Input data-testid={`admin-user-edit-prenom-${u.Utilisateur_ID}`} value={editForm.prenom} onChange={(e) => setEditForm({ ...editForm, prenom: e.target.value })} className="bg-[#0A0D12] border-slate-700 text-slate-100" />
                <Input data-testid={`admin-user-edit-nom-${u.Utilisateur_ID}`} value={editForm.nom} onChange={(e) => setEditForm({ ...editForm, nom: e.target.value })} className="bg-[#0A0D12] border-slate-700 text-slate-100" />
                <Input data-testid={`admin-user-edit-email-${u.Utilisateur_ID}`} value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className="bg-[#0A0D12] border-slate-700 text-slate-100 sm:col-span-2" />
                <RoleCheckboxes selected={editForm.roles || []} onChange={(roles) => setEditForm({ ...editForm, roles })} roles={rolesCatalog} idPrefix={`admin-user-edit-${u.Utilisateur_ID}`} />
                <SiteCheckboxes selected={editForm.sites || []} onChange={(sitesSel) => setEditForm({ ...editForm, sites: sitesSel })} sites={sites} idPrefix={`admin-user-edit-${u.Utilisateur_ID}`} />
                {error && <div className="sm:col-span-2 text-sm text-red-400">{error}</div>}
                <div className="sm:col-span-2 flex gap-2">
                  <Button data-testid={`admin-user-edit-save-${u.Utilisateur_ID}`} disabled={busy || (editForm.roles || []).length === 0 || (editForm.sites || []).length === 0} onClick={saveEdit} size="sm" className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-xs">
                    <Check className="w-3.5 h-3.5 mr-1" /> Enregistrer
                  </Button>
                  <Button data-testid={`admin-user-edit-cancel-${u.Utilisateur_ID}`} onClick={() => setEditingId(null)} size="sm" variant="outline" className="border-slate-700 text-xs">
                    <X className="w-3.5 h-3.5 mr-1" /> Annuler
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="text-sm font-semibold text-slate-100">
                    {u.Nom_affiche} <span className="text-cyan-500 font-mono text-xs ml-1">{(u.Roles || []).map((r) => roleLabel(r, rolesCatalog)).join(" · ")}</span>
                    <span className="text-slate-500 font-mono text-xs ml-1">· {(u.Sites || []).join(" · ")}</span>
                  </div>
                  <div className="text-xs text-slate-500">{u.Email}</div>
                </div>
                <div className="flex gap-1.5">
                  <Button data-testid={`admin-user-edit-${u.Utilisateur_ID}`} size="sm" variant="outline" onClick={() => startEdit(u)} className="border-slate-700 text-xs">
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button data-testid={`admin-user-toggle-${u.Utilisateur_ID}`} size="sm" variant="outline" onClick={() => toggleActif(u)} className="border-slate-700 text-xs">
                    {String(u.Actif).toUpperCase() === "TRUE" ? "Désactiver" : "Activer"}
                  </Button>
                  {deletingId === u.Utilisateur_ID ? (
                    <>
                      <Button data-testid={`admin-user-delete-confirm-${u.Utilisateur_ID}`} disabled={busy} size="sm" onClick={() => remove(u)} className="bg-red-600 hover:bg-red-500 text-white text-xs">
                        Confirmer
                      </Button>
                      <Button data-testid={`admin-user-delete-cancel-${u.Utilisateur_ID}`} size="sm" variant="outline" onClick={() => setDeletingId(null)} className="border-slate-700 text-xs">
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </>
                  ) : (
                    <Button data-testid={`admin-user-delete-${u.Utilisateur_ID}`} size="sm" variant="outline" onClick={() => setDeletingId(u.Utilisateur_ID)} className="border-red-900 text-red-400 hover:bg-red-950 text-xs">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
